import React, { useMemo } from "react";
import { Card, Col, Divider, Grid, Row, Typography } from "antd";

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

type Block =
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | { type: "note"; text: string };

type Section = {
  id: string;
  title: string;
  blocks: Block[];
};

type DocGroup = {
  id: string;
  title: string;
  sections: Section[];
};

const TermsRegisterView = ({
  contactEmail = "support@platformb2b.com",
  hotline = "1900-xxxx",
  lastUpdated = "22/08/2025",
  platformName = "AI Procurement",
  companyOwner = "Apetechs",
}: {
  contactEmail?: string;
  hotline?: string;
  lastUpdated?: string;
  platformName?: string;
  companyOwner?: string;
}) => {
  const groups: DocGroup[] = useMemo(
    () => [
      // I. ĐIỀU KHOẢN & ĐIỀU KIỆN ĐĂNG KÝ TÀI KHOẢN
      {
        id: "dkdk",
        title: "I. Điều khoản & Điều kiện Đăng ký Tài khoản",
        sections: [
          {
            id: "gioi-thieu",
            title: "1. Giới thiệu",
            blocks: [
              {
                type: "paragraph",
                text: `Việc đăng ký tài khoản trên nền tảng ${platformName} đồng nghĩa với việc bạn đồng ý tuân thủ các Điều khoản & Điều kiện dưới đây, cùng với Chính sách Bảo mật, Chính sách Cookie và Điều khoản Dịch vụ.`,
              },
            ],
          },
          {
            id: "thong-tin-dang-ky",
            title: "2. Thông tin đăng ký",
            blocks: [
              {
                type: "list",
                items: [
                  "Người dùng phải cung cấp thông tin chính xác, đầy đủ và cập nhật khi đăng ký.",
                  "Nghiêm cấm việc sử dụng thông tin giả mạo, thông tin của người khác hoặc tài khoản trùng lặp.",
                  "Chúng tôi có quyền từ chối hoặc huỷ bỏ tài khoản nếu phát hiện thông tin không hợp lệ.",
                ],
              },
            ],
          },
          {
            id: "bao-mat-tai-khoan",
            title: "3. Bảo mật tài khoản",
            blocks: [
              {
                type: "list",
                items: [
                  "Người dùng chịu trách nhiệm giữ bí mật tên đăng nhập và mật khẩu.",
                  "Nếu phát hiện truy cập trái phép, người dùng phải thông báo ngay cho chúng tôi.",
                  "Chúng tôi không chịu trách nhiệm cho thiệt hại phát sinh từ việc người dùng không bảo mật thông tin đăng nhập.",
                ],
              },
            ],
          },
          {
            id: "quyen-nghia-vu-nguoi-dung",
            title: "4. Quyền và nghĩa vụ của người dùng",
            blocks: [
              { type: "paragraph", text: "Người dùng cam kết:" },
              {
                type: "list",
                items: [
                  "Sử dụng tài khoản đúng mục đích, tuân thủ pháp luật và Điều khoản Dịch vụ.",
                  "Không được cho thuê, bán, chuyển nhượng tài khoản.",
                  "Không sử dụng tài khoản để thực hiện hành vi gian lận, phát tán mã độc, hoặc gây hại cho hệ thống.",
                ],
              },
            ],
          },
          {
            id: "quyen-nghia-vu-chung-toi",
            title: "5. Quyền và nghĩa vụ của chúng tôi",
            blocks: [
              {
                type: "list",
                items: [
                  "Chúng tôi có quyền kiểm tra, tạm khoá hoặc xoá tài khoản nếu phát hiện vi phạm.",
                  "Có quyền yêu cầu người dùng xác minh danh tính (qua email, số điện thoại, giấy tờ doanh nghiệp).",
                  "Bảo vệ dữ liệu cá nhân theo đúng Chính sách Bảo mật đã công bố.",
                ],
              },
            ],
          },
          {
            id: "cham-dut-tai-khoan",
            title: "6. Chấm dứt tài khoản",
            blocks: [
              {
                type: "list",
                items: [
                  "Người dùng có thể yêu cầu chấm dứt tài khoản bất kỳ lúc nào.",
                  "Chúng tôi có quyền xoá tài khoản không hoạt động trong thời gian dài hoặc vi phạm điều khoản.",
                  "Khi tài khoản bị chấm dứt, một số dữ liệu có thể được lưu giữ theo yêu cầu pháp luật.",
                ],
              },
            ],
          },
          {
            id: "so-huu-tri-tue",
            title: "7. Sở hữu trí tuệ",
            blocks: [
              {
                type: "list",
                items: [
                  `Toàn bộ mã nguồn, thuật toán AI, giao diện, logo thuộc sở hữu của Công ty Cổ phần ${companyOwner}.`,
                  "Người dùng không được sao chép, chỉnh sửa, phân phối lại nếu chưa có sự đồng ý bằng văn bản.",
                ],
              },
            ],
          },
          {
            id: "luat-ap-dung",
            title: "8. Luật áp dụng và giải quyết tranh chấp",
            blocks: [
              {
                type: "list",
                items: [
                  "Điều khoản này được điều chỉnh theo pháp luật Việt Nam (hoặc pháp luật quốc tế tuỳ thị trường mục tiêu).",
                  "Tranh chấp sẽ được giải quyết thông qua thương lượng. Nếu không đạt kết quả, sẽ đưa ra trọng tài hoặc toà án có thẩm quyền.",
                ],
              },
            ],
          },
          {
            id: "thay-doi-dieu-khoan-dk",
            title: "9. Thay đổi điều khoản (Đăng ký tài khoản)",
            blocks: [
              {
                type: "paragraph",
                text: "Chúng tôi có thể cập nhật Điều khoản & Điều kiện Đăng ký Tài khoản. Phiên bản mới sẽ được thông báo trên nền tảng kèm ngày hiệu lực.",
              },
            ],
          },
        ],
      },

      // II. ĐIỀU KHOẢN DỊCH VỤ
      {
        id: "dkdv",
        title: "II. Điều khoản Dịch vụ",
        sections: [
          {
            id: "dkdv-gioi-thieu",
            title: "1. Giới thiệu",
            blocks: [
              {
                type: "paragraph",
                text: `Chào mừng bạn đến với nền tảng ${platformName}. Khi sử dụng dịch vụ của chúng tôi, bạn đồng ý tuân thủ các Điều khoản Dịch vụ này.`,
              },
            ],
          },
          {
            id: "pham-vi-dich-vu",
            title: "2. Phạm vi dịch vụ",
            blocks: [
              {
                type: "list",
                items: [
                  "Quản lý yêu cầu báo giá (RFQ), báo giá.",
                  "Gợi ý nhà cung cấp và tối ưu chi phí bằng AI.",
                  "Quản trị giao dịch, dữ liệu.",
                ],
              },
            ],
          },
          {
            id: "tai-khoan-bao-mat",
            title: "3. Tài khoản và bảo mật",
            blocks: [
              {
                type: "list",
                items: [
                  "Người dùng phải cung cấp thông tin chính xác khi đăng ký tài khoản.",
                  "Bạn chịu trách nhiệm giữ bí mật thông tin đăng nhập.",
                  "Mọi hành vi phát sinh từ tài khoản sẽ được coi là do chính bạn thực hiện.",
                ],
              },
            ],
          },
          {
            id: "quyen-nguoi-dung-dkdv",
            title: "4. Quyền và nghĩa vụ của người dùng",
            blocks: [
              {
                type: "list",
                items: [
                  "Không sử dụng dịch vụ vào mục đích gian lận, vi phạm pháp luật.",
                  "Không can thiệp trái phép vào hệ thống hoặc dữ liệu của người khác.",
                  "Tuân thủ chính sách bảo mật và chính sách cookie của chúng tôi.",
                ],
              },
            ],
          },
          {
            id: "quyen-chung-toi-dkdv",
            title: "5. Quyền và nghĩa vụ của chúng tôi",
            blocks: [
              {
                type: "list",
                items: [
                  "Tạm ngưng hoặc chấm dứt tài khoản nếu phát hiện vi phạm.",
                  "Nâng cấp, thay đổi, gián đoạn dịch vụ để bảo trì hoặc cải tiến.",
                  "Thu thập và xử lý dữ liệu nhằm nâng cao chất lượng dịch vụ, theo Chính sách Bảo mật.",
                ],
              },
            ],
          },
          {
            id: "phi-dich-vu",
            title: "6. Thanh toán và phí dịch vụ",
            blocks: [
              {
                type: "list",
                items: [
                  "Một số tính năng có thể yêu cầu phí dịch vụ hoặc gói đăng ký.",
                  "Người dùng có trách nhiệm thanh toán đầy đủ, đúng hạn.",
                  "Phí đã thanh toán thường không hoàn lại, trừ khi có thỏa thuận khác.",
                ],
              },
            ],
          },
          {
            id: "gioi-han-trach-nhiem",
            title: "7. Giới hạn trách nhiệm",
            blocks: [
              {
                type: "list",
                items: [
                  "Không chịu trách nhiệm cho thiệt hại gián tiếp, mất lợi nhuận hoặc dữ liệu do việc sử dụng dịch vụ.",
                  "Dữ liệu và khuyến nghị từ AI chỉ mang tính tham khảo; người dùng tự chịu trách nhiệm với quyết định mua sắm.",
                ],
              },
            ],
          },
          {
            id: "so-huu-tri-tue-dkdv",
            title: "8. Sở hữu trí tuệ",
            blocks: [
              {
                type: "list",
                items: [
                  `Toàn bộ mã nguồn, thuật toán AI, giao diện, logo thuộc sở hữu của Công ty Cổ phần ${companyOwner}.`,
                  "Người dùng không được sao chép, chỉnh sửa, phân phối lại nếu chưa có sự đồng ý bằng văn bản.",
                ],
              },
            ],
          },
          {
            id: "luat-tranh-chap-dkdv",
            title: "9. Luật áp dụng và giải quyết tranh chấp",
            blocks: [
              {
                type: "list",
                items: [
                  "Điều khoản này được điều chỉnh theo pháp luật Việt Nam (hoặc theo thị trường mục tiêu).",
                  "Tranh chấp giải quyết qua thương lượng; nếu không thành, đưa ra trọng tài hoặc toà án có thẩm quyền.",
                ],
              },
            ],
          },
          {
            id: "thay-doi-dieu-khoan-dvdv",
            title: "10. Thay đổi điều khoản (Dịch vụ)",
            blocks: [
              {
                type: "paragraph",
                text: "Chúng tôi có thể cập nhật Điều khoản Dịch vụ. Phiên bản mới sẽ được thông báo trên nền tảng kèm ngày hiệu lực.",
              },
            ],
          },
        ],
      },

      // III. CHÍNH SÁCH BẢO MẬT
      {
        id: "csbm",
        title: "III. Chính sách Bảo mật",
        sections: [
          {
            id: "csbm-gioi-thieu",
            title: "1. Giới thiệu",
            blocks: [
              {
                type: "paragraph",
                text: `Chính sách này giải thích cách chúng tôi thu thập, sử dụng, lưu trữ và bảo vệ dữ liệu cá nhân khi bạn sử dụng ${platformName}. Tất cả thông tin người dùng cung cấp có thể được dùng để phục vụ công tác phát triển nền tảng.`,
              },
            ],
          },
          {
            id: "pham-vi-ap-dung",
            title: "2. Phạm vi áp dụng",
            blocks: [
              {
                type: "paragraph",
                text: "Áp dụng cho tất cả người dùng, khách hàng, nhà cung cấp, đối tác truy cập và sử dụng qua website, ứng dụng, API hoặc các kênh tích hợp khác.",
              },
            ],
          },
          {
            id: "du-lieu-can-cung-cap",
            title: "3. Dữ liệu cần cung cấp",
            blocks: [
              {
                type: "list",
                items: [
                  "Thông tin định danh: họ tên, email, số điện thoại, chức vụ, doanh nghiệp.",
                  "Thông tin đăng nhập: tài khoản, mật khẩu (được mã hoá).",
                  "Thông tin tài khoản thanh toán.",
                  "Dữ liệu giao dịch: yêu cầu báo giá (RFQ), báo giá.",
                  "Thông tin kỹ thuật: địa chỉ IP, trình duyệt, thiết bị, nhật ký truy cập.",
                  "Dữ liệu AI/ML: thông tin xử lý phục vụ gợi ý tối ưu chi phí, lựa chọn nhà cung cấp, phân tích rủi ro.",
                ],
              },
            ],
          },
          {
            id: "muc-dich-su-dung",
            title: "4. Mục đích sử dụng dữ liệu",
            blocks: [
              {
                type: "list",
                items: [
                  "Cung cấp dịch vụ mua hàng thông minh dựa trên AI.",
                  "Quản lý giao dịch, thanh toán.",
                  "Tối ưu chi phí và gợi ý nhà cung cấp.",
                  "Nâng cao trải nghiệm người dùng qua phân tích hành vi.",
                  "Đảm bảo an toàn, ngăn chặn gian lận và tuân thủ pháp luật.",
                  "Phát triển nền tảng.",
                ],
              },
            ],
          },
          {
            id: "chia-se-du-lieu",
            title: "5. Chia sẻ dữ liệu",
            blocks: [
              {
                type: "paragraph",
                text: "Chỉ chia sẻ với: (i) nhà cung cấp/đối tác liên quan trực tiếp; (ii) đơn vị hạ tầng (cloud, AI engine, bảo mật); (iii) cơ quan nhà nước khi có yêu cầu hợp pháp.",
              },
            ],
          },
          {
            id: "bao-mat-du-lieu",
            title: "6. Bảo mật dữ liệu",
            blocks: [
              {
                type: "paragraph",
                text: "Áp dụng và hướng tới tuân thủ ISO/IEC 27001:2022.",
              },
            ],
          },
          {
            id: "quyen-nguoi-dung",
            title: "7. Quyền của người dùng",
            blocks: [
              {
                type: "list",
                items: [
                  "Truy cập, chỉnh sửa dữ liệu cá nhân.",
                  "Sử dụng nguồn dữ liệu trên nền tảng theo quy tắc và gói đăng ký.",
                ],
              },
            ],
          },
          {
            id: "thoi-gian-luu-tru",
            title: "8. Thời gian lưu trữ",
            blocks: [
              {
                type: "paragraph",
                text: "Lưu trữ trong thời gian cần thiết để cung cấp dịch vụ và theo quy định pháp luật hiện hành.",
              },
            ],
          },
          {
            id: "thay-doi-chinh-sach",
            title: "9. Thay đổi chính sách",
            blocks: [
              {
                type: "paragraph",
                text: "Chính sách có thể được cập nhật để phù hợp pháp luật và hoạt động kinh doanh. Phiên bản mới sẽ được công bố kèm ngày hiệu lực.",
              },
            ],
          },
          {
            id: "lien-he",
            title: "10. Liên hệ",
            blocks: [
              {
                type: "note",
                text: `Hỗ trợ: ${contactEmail} • Hotline: ${hotline}`,
              },
            ],
          },
        ],
      },
    ],
    [platformName, companyOwner, contactEmail, hotline]
  );

  return (
    <div className="pp-container">
      <Row gutter={16} className="pp-grid">
        <Col xs={24} lg={24}>
          <Title level={1} className="pp-title">
            {platformName}: Điều khoản & Chính sách
          </Title>
          <Text className="pp-subtle">
            Cập nhật: {lastUpdated} • Chủ sở hữu: {companyOwner}
          </Text>

          <Card size="small" className="pp-main-card">
            {groups.map((g, gi) => (
              <div key={g.id}>
                <Title level={2} id={g.id} className="pp-group-title">
                  {g.title}
                </Title>

                {g.sections.map((s, si) => (
                  <div key={s.id}>
                    <section id={s.id} className="pp-section">
                      <Title level={3} className="pp-section-title">
                        {s.title}
                      </Title>

                      {s.blocks.map((b, i) => {
                        if (b.type === "paragraph") {
                          return (
                            <Paragraph key={i} className="pp-block">
                              {b.text}
                            </Paragraph>
                          );
                        }
                        if (b.type === "list") {
                          return (
                            <ul key={i} className="pp-list pp-block">
                              {b.items.map((it, j) => (
                                <li key={j}>{it}</li>
                              ))}
                            </ul>
                          );
                        }
                        if (b.type === "note") {
                          return (
                            <div key={i} className="pp-note pp-block">
                              {b.text}
                            </div>
                          );
                        }
                        return null;
                      })}
                    </section>

                    {si < g.sections.length - 1 && (
                      <Divider className="pp-divider" />
                    )}
                  </div>
                ))}

                {gi < groups.length - 1 && <Divider className="pp-divider" />}
              </div>
            ))}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TermsRegisterView;
